import React, { CSSProperties, ReactElement, useMemo, useState } from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
	DragStart,
	DragUpdate,
	Droppable,
	DroppableProvided,
	DroppableStateSnapshot,
	DropResult,
	ResponderProvided,
	Sensor,
	useMouseSensor,
	useTouchSensor
} from 'react-beautiful-dnd';
import { arrayMove } from '@dnd-kit/sortable';

import { AttachmentCard } from '..';
import { AttachmentType } from '../../../models';
import { Attachment } from '../../../redux';
import { hashString } from '../../../utils';
import { Box } from '@material-ui/core';

import './sortable-attachment-list.styles.scss';

export interface ISortableAttachmentListProps {
	attachments: Attachment[];
	activeType?: AttachmentType;
	allowAuthorContentEditing?: boolean;
	highlightedAttachment?: Attachment;
	reorderAttachments: (attachments: Attachment[]) => void;
	onDeleteAttachment: (attachment: Attachment) => void;
	onEditAttachment: (attachmen: Attachment) => void;
}

export function SortableAttachmentList(
	props: ISortableAttachmentListProps
): ReactElement {
	const {
		attachments,
		highlightedAttachment,
		activeType,
		allowAuthorContentEditing,
		reorderAttachments,
		onDeleteAttachment,
		onEditAttachment,
	} = props;

	const filteredAttachments: Attachment[] = useMemo(() => {
		return attachments
			.filter((attachment: Attachment) => {
				if (activeType === undefined) return true;
				return attachment.type === activeType;
			})
			.sort((attachment1: Attachment, attachment2: Attachment) => {
				return attachment1.order > attachment2.order ? 1 : -1;
			});
	}, [activeType, attachments]);

	const sensors: Sensor[] = [useMouseSensor, useTouchSensor];

	const queryAttr: string = 'data-rbd-drag-handle-draggable-id';
	const [placeholderProps, setPlaceholderProps] = useState<{
		clientX?: number;
		clientY?: number;
		clientWidth?: number;
		clientHeight?: number;
	}>({});

	function getItemStyle(draggableStyle: CSSProperties): CSSProperties {
		return {
			userSelect: 'none',
			zIndex: 2,
			margin: `${1.2}rem 0`,
			...draggableStyle
		};
	}

	function sortAttachments(result: DropResult, _: ResponderProvided): void {
		setPlaceholderProps({});
		const sourceIndex: number = result.source.index;
		const destinationIndex: number | undefined = result.destination?.index;

		if (destinationIndex === undefined || destinationIndex === sourceIndex)
			return;

		const newAttachments: Attachment[] = arrayMove(
			attachments,
			sourceIndex,
			destinationIndex
		);

		reorderAttachments(newAttachments);
	}

	function handleDragStart(initial: DragStart, _: ResponderProvided): void {
		const draggedDOM: Element | null = getDraggedDom(initial.draggableId);

		if (draggedDOM === null || draggedDOM.parentNode === null) {
			return;
		}

		const { clientHeight, clientWidth } = draggedDOM;
		const sourceIndex: number = initial.source.index;
		const clientY: number =
			parseFloat(
				window.getComputedStyle(draggedDOM.parentNode as Element)
					.paddingTop
			) +
			[...(draggedDOM.parentNode.children as any)]
				.slice(0, sourceIndex)
				.reduce((total, curr) => {
					const style: CSSStyleDeclaration =
						curr.currentStyle || window.getComputedStyle(curr);
					const marginBottom: number = parseFloat(style.marginBottom);
					return total + curr.clientHeight + marginBottom;
				}, 0);

		setPlaceholderProps({
			clientHeight,
			clientWidth,
			clientY,
			clientX: parseFloat(
				window.getComputedStyle(draggedDOM.parentNode as Element)
					.paddingLeft
			)
		});
	}

	function getDraggedDom(draggableId: string): Element | null {
		const domQuery: string = `[${queryAttr}='${draggableId}']`;
		return document.querySelector(domQuery);
	}

	function handleDragUpdate(initial: DragUpdate, _: ResponderProvided): void {
		if (!initial.destination) {
			return;
		}

		const draggedDOM: Element | null = getDraggedDom(initial.draggableId);

		if (draggedDOM === null || draggedDOM.parentNode === null) {
			return;
		}

		const { clientHeight, clientWidth } = draggedDOM;
		const destinationIndex: number = initial.destination.index;
		const sourceIndex: number = initial.source.index;

		const childrenArray: Element[] = [
			...(draggedDOM.parentNode.children as any)
		];
		const movedItem: Element = childrenArray[sourceIndex];
		childrenArray.splice(sourceIndex, 1);

		const updatedArray: Element[] = [
			...childrenArray.slice(0, destinationIndex),
			movedItem,
			...childrenArray.slice(destinationIndex + 1)
		];

		const clientY: number =
			parseFloat(
				window.getComputedStyle(draggedDOM.parentNode as Element)
					.paddingTop
			) +
			updatedArray
				.slice(0, destinationIndex)
				.reduce((total: number, curr: any) => {
					const style =
						curr.currentStyle || window.getComputedStyle(curr);
					const marginBottom = parseFloat(style.marginBottom);
					return total + curr.clientHeight + marginBottom;
				}, 0);

		setPlaceholderProps({
			clientHeight,
			clientWidth,
			clientY,
			clientX: parseFloat(
				window.getComputedStyle(draggedDOM.parentNode as Element)
					.paddingLeft
			)
		});
	}

	return (
		<Box className="sortable-attachment-list">
			<DragDropContext
				onDragStart={handleDragStart}
				onDragUpdate={handleDragUpdate}
				onDragEnd={sortAttachments}
				sensors={sensors}
			>
				<Droppable droppableId="attachment-droppable">
					{(
						droppableProvided: DroppableProvided,
						droppableSnapshot: DroppableStateSnapshot
					) => (
						<>
							<div
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
								className="attachment-list-droppable-container"
							>
								{filteredAttachments.map(
									(attachment: Attachment, index: number) => (
										<Draggable
											key={hashString(
												`${index}-${attachment.order}-${attachment.title}-${attachment.type}-${attachment.description}`
											)}
											draggableId={`${attachment.order}`}
											index={index}
											isDragDisabled={
												activeType !== undefined
											}
										>
											{(
												provided: DraggableProvided,
												snapshot: DraggableStateSnapshot
											) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													style={getItemStyle(
														provided.draggableProps
															.style as CSSProperties
													)}
												>
													<AttachmentCard
														attachment={attachment}
														isHighlighted={highlightedAttachment?.order === attachment.order}
														allowAuthorContentEditing={
															allowAuthorContentEditing
														}
														onDelete={() =>
															onDeleteAttachment(
																attachment
															)
														}
														onEdit={
															onEditAttachment
														}
														isDragging={
															snapshot.isDragging
														}
													/>
												</div>
											)}
										</Draggable>
									)
								)}
								{droppableProvided.placeholder}
							</div>
							{placeholderProps !== {} &&
								droppableSnapshot.isDraggingOver && (
									<div
										className="placeholder"
										style={{
											top: placeholderProps.clientY,
											left: placeholderProps.clientX,
											height: placeholderProps.clientHeight,
											width: placeholderProps.clientWidth
										}}
									/>
								)}
						</>
					)}
				</Droppable>
			</DragDropContext>
		</Box>
	);
}
